import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Redirect } from 'react-router';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { agent, getErrorMessage } from '@shared/services/agent';
import { getParameterByName } from '@shared/utils/UrlUtils';
import AuthWrapper from '@features/auth/pages/auth/components/AuthWrapper';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

interface AuthorizationRequestInfo {
  clientName?: string;
  companyMcpEnabled?: boolean;
  appName?: string;
  destination?: string;
  companyName?: string;
  appApproved?: boolean;
  canApproveApp?: boolean;
  unapprovedRedirectUris?: string[];
}

// Inside another site's frame this page could be overlaid to trick a click on Allow (clickjacking).
// A frame that blocks scripts never renders the React app at all, so this check covers both cases.
function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * OAuth consent screen for MCP clients (e.g. claude.ai). The authorization server redirects the
 * browser here (carrying the OAuth query params); the user — signed in with their normal TeamToday
 * session — approves, and we hand the request to the backend which mints the authorization code and
 * returns the client's redirect URL.
 *
 * Who the app is comes from the backend (appName: Claude, an app on your computer, or the host its
 * codes go to), never from the name the app registered itself with. Apps other than Claude and
 * localhost need a company admin to approve them for the company first.
 */
export default function McpConsentPage() {
  const currentUser = useSelector(selectCurrentUser);

  const clientId = getParameterByName('client_id', window.location.href) || '';
  const redirectUri = getParameterByName('redirect_uri', window.location.href) || '';
  const codeChallenge = getParameterByName('code_challenge', window.location.href) || '';
  const codeChallengeMethod = getParameterByName('code_challenge_method', window.location.href) || '';
  const state = getParameterByName('state', window.location.href) || '';
  const scope = getParameterByName('scope', window.location.href) || '';
  const responseType = getParameterByName('response_type', window.location.href) || '';

  const [info, setInfo] = useState<AuthorizationRequestInfo>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const framed = isFramed();

  useEffect(() => {
    if (framed || !currentUser || !clientId || !redirectUri) return;
    // The backend validates client_id + redirect_uri here; a failure means we must NOT render the
    // redirecting Allow/Deny buttons (an unvalidated redirect_uri would be an open redirect on Deny).
    agent.requests
      .get(`/api/mcp-oauth/authorization-request?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`)
      .then((res: AuthorizationRequestInfo) => setInfo(res || {}))
      .catch(() => setLoadError('This authorization request is invalid or has expired. Please start again from the application you were connecting.'))
      .finally(() => setLoading(false));
  }, [framed, currentUser, clientId, redirectUri]);

  if (framed) {
    return (
      <AuthWrapper dataTest="mcp-consent-framed" title="Authorization" hideLoginLink={true}>
        <BodyRegular>For your security this page can't be shown inside another website. Open it in its own browser tab to continue.</BodyRegular>
      </AuthWrapper>
    );
  }

  // The user must be signed in to approve. (Return-to-consent after a fresh login is a follow-up;
  // in the normal flow the admin connecting Claude is already signed in.)
  if (!currentUser) {
    return <Redirect to="/login" />;
  }

  if (!clientId || !redirectUri) {
    return (
      <AuthWrapper dataTest="mcp-consent-invalid" title="Authorization" hideLoginLink={true}>
        <BodyRegular>This authorization request is missing required parameters.</BodyRegular>
      </AuthWrapper>
    );
  }

  const appName = info.appName || 'An app';
  const clientName = info.clientName;
  const companyName = info.companyName || 'your company';
  // The self-chosen name is only worth showing when it adds something to the name we gave the app
  const callsItself = clientName && clientName !== appName
    ? <BodySmall style={{ marginBottom: 8 }}>It calls itself &ldquo;{clientName}&rdquo;.</BodySmall>
    : null;

  const handleAllow = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await agent.requests.post('/api/mcp-oauth/approve', {
        clientId,
        redirectUri,
        scope,
        state,
        codeChallenge,
        codeChallengeMethod,
        responseType,
      });
      window.location.assign(res.redirectUri);
    } catch (e) {
      setSubmitting(false);
      setError(getErrorMessage(e, 'Something went wrong approving access. Please try again.'));
    }
  };

  const handleApproveAppAndAllow = async () => {
    setSubmitting(true);
    setError('');
    try {
      await agent.requests.post('/api/mcp-oauth/approve-app', { clientId, redirectUri });
    } catch (e) {
      setSubmitting(false);
      setError(getErrorMessage(e, 'Something went wrong approving this app. Please try again.'));
      return;
    }
    await handleAllow();
  };

  const handleDeny = () => {
    // Never send anyone to an app the company hasn't approved, even with an error and no code
    if (info.appApproved === false) {
      setCancelled(true);
      return;
    }
    const sep = redirectUri.includes('?') ? '&' : '?';
    let denyUri = `${redirectUri}${sep}error=access_denied`;
    if (state) denyUri += `&state=${encodeURIComponent(state)}`;
    window.location.assign(denyUri);
  };

  if (loading) {
    return <AuthWrapper dataTest="mcp-consent-loading" title="Authorization" hideLoginLink={true} spinner={true} />;
  }

  // Load failed / request not validated by the backend: terminal screen with no redirecting buttons.
  if (loadError) {
    return (
      <AuthWrapper dataTest="mcp-consent-error" title="Authorization" hideLoginLink={true}>
        <BodyRegular>{loadError}</BodyRegular>
      </AuthWrapper>
    );
  }

  if (cancelled) {
    return (
      <AuthWrapper dataTest="mcp-consent-cancelled" title="Authorization" hideLoginLink={true}>
        <BodyRegular>Nothing was connected. You can close this window.</BodyRegular>
      </AuthWrapper>
    );
  }

  if (info.companyMcpEnabled === false) {
    return (
      <AuthWrapper dataTest="mcp-consent-disabled" title="Authorize access" hideLoginLink={true}>
        <BodyRegular style={{ marginBottom: 16 }}>
          MCP integration isn't enabled for your company.
        </BodyRegular>
        <BodySmall style={{ marginBottom: 24 }}>
          Ask a company admin to enable it under Settings &rarr; Other before connecting <strong>{appName}</strong>.
        </BodySmall>
        <OutlineButton
          dataTest="mcp-consent-cancel"
          text="Cancel"
          fullWidth={true}
          size={ButtonSize.Large}
          click={handleDeny}
        />
      </AuthWrapper>
    );
  }

  // Not approved for the company: never redirect to it (no Deny link either), only an admin can go on.
  // Strict false check so an older API without app approval (during a rollout) keeps the normal screen.
  if (info.appApproved === false) {
    if (!info.canApproveApp) {
      return (
        <AuthWrapper dataTest="mcp-consent-needs-admin" title="Authorize access" hideLoginLink={true}>
          <BodyRegular style={{ marginBottom: 16 }}>
            <strong>{appName}</strong> isn't approved for {companyName} yet.
          </BodyRegular>
          {callsItself}
          <BodySmall style={{ marginBottom: 8 }}>
            A company admin needs to approve it before anyone can connect it. Ask them to connect it once from
            their own account. After that you can connect it too.
          </BodySmall>
        </AuthWrapper>
      );
    }
    return (
      <AuthWrapper dataTest="mcp-consent-approve-app" title="Approve this app" hideLoginLink={true}>
        <BodyRegular style={{ marginBottom: 16 }}>
          <strong>{appName}</strong> isn't approved for {companyName} yet.
        </BodyRegular>
        {callsItself}
        <BodySmall style={{ marginBottom: 8 }}>
          As a company admin you can approve it for everyone in {companyName}. It will then be able to read
          whereabouts and offices for anyone who connects it, as you can see them in TeamToday.
        </BodySmall>
        <BodySmall style={{ marginBottom: 8 }}>
          Approving lets it receive sign-in codes at:
        </BodySmall>
        <AddressList>
          {(info.unapprovedRedirectUris || []).map(uri => <li key={uri}><code>{uri}</code></li>)}
        </AddressList>
        <BodySmall style={{ marginBottom: 24 }}>
          Only approve apps you recognise. After you allow access you'll return to <strong>{info.destination}</strong>.
        </BodySmall>

        {error ? <BodySmall style={{ marginBottom: 16, color: '#F0224F' }}>{error}</BodySmall> : null}

        <PrimaryButton
          dataTest="mcp-consent-approve-app-allow"
          text="Approve and allow access"
          fullWidth={true}
          size={ButtonSize.Large}
          disabled={submitting}
          click={handleApproveAppAndAllow}
          style={{ marginBottom: 12 }}
        />
        <OutlineButton
          dataTest="mcp-consent-approve-app-cancel"
          text="Cancel"
          fullWidth={true}
          size={ButtonSize.Large}
          disabled={submitting}
          click={() => setCancelled(true)}
        />
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper dataTest="mcp-consent" title="Authorize access" hideLoginLink={true}>
      <BodyRegular style={{ marginBottom: 16 }}>
        <strong>{appName}</strong> wants to access your TeamToday account as <strong>{currentUser.email}</strong>.
      </BodyRegular>
      {callsItself}
      <BodySmall style={{ marginBottom: 8 }}>
        It will be able to read whereabouts and offices in {companyName} on your behalf, as you can see them in TeamToday.
      </BodySmall>
      <BodySmall style={{ marginBottom: 8 }}>
        After you allow access you'll return to <strong>{info.destination}</strong>.
      </BodySmall>
      {scope ? <BodySmall style={{ marginBottom: 24 }}>Requested scope: {scope}</BodySmall> : <div style={{ marginBottom: 24 }} />}

      {error ? <BodySmall style={{ marginBottom: 16, color: '#F0224F' }}>{error}</BodySmall> : null}

      <PrimaryButton
        dataTest="mcp-consent-allow"
        text="Allow access"
        fullWidth={true}
        size={ButtonSize.Large}
        disabled={submitting}
        click={handleAllow}
        style={{ marginBottom: 12 }}
      />
      <OutlineButton
        dataTest="mcp-consent-deny"
        text="Deny"
        fullWidth={true}
        size={ButtonSize.Large}
        disabled={submitting}
        click={handleDeny}
      />
    </AuthWrapper>
  );
}

const AddressList = styled.ul`
  margin: 0 0 16px 0;
  padding-left: 20px;
  font-size: 13px;
  word-break: break-all;
`;
